import { useRef, useState } from 'react';

import { Icon } from '../components/Icon.js';
import { Menu, MenuGroupTitle, MenuItem, MenuSeparator } from '../components/Menu.js';
import { ENDING_META, STAGE_META } from '../conversation/stageMeta.js';
import type { ChannelId } from '../fixtures/domain.js';
import { HIRING_STAGES, closingVariantFor, type BulkAction } from '../fixtures/rules.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './BulkActions.module.css';

interface BulkActionsProps {
  /** The eligible ids per bulk action over the picked set (see `bulkTargets`),
   *  which the menu greys its rows and counts its group title off. */
  targets: Record<BulkAction, ChannelId[]>;
  /** How many of `targets.end` the ending also rejects: the job-bounded ones.
   *  Only names the row; the dialog counts its own from the same derivation. */
  rejectReach: number;
  /** Open the compose surface for the selected set. */
  onSend: () => void;
  /** Run a hiring stage or End over the picked set: the "…" rows the dialogs
   *  hang off (see InboxPanel). */
  onAction: (action: BulkAction) => void;
  /** Open the "add to list" dialog for the selected set (the "…" overflow). */
  onAddToList: () => void;
  /** The list the inbox is scoped to, or null, which gates "Remove from list". */
  listName: string | null;
  onRemoveFromList: () => void;
}

/**
 * What an employer does to a picked set, in the header's tool slot while a
 * selection is up: Message as the primary, the rest behind the "…". A stage row
 * greys where no picked chat has a job behind it.
 */
export function BulkActions({
  targets,
  rejectReach,
  onSend,
  onAction,
  onAddToList,
  listName,
  onRemoveFromList,
}: BulkActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  useDismiss(menuOpen, () => setMenuOpen(false), overflowRef);

  // The three stages read one rule, so any one's reach stands for the group's.
  const stageReach = targets[HIRING_STAGES[0]].length;
  // The ending names itself off what it will do, by the rule every single-chat
  // surface uses. See the row.
  const ending = ENDING_META[closingVariantFor(rejectReach > 0)];

  return (
    /* One split button: both halves wear the accent fill and share a seam,
       rather than a filled primary beside a detached icon button. */
    <div className={styles.group} ref={overflowRef}>
      <button type="button" className={styles.primary} onClick={onSend}>
        Message
      </button>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="More actions"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <Icon name="more" size={18} />
      </button>
      {/* Downward at every width: this sits in the header, so up would open
          through the search box and off the top of the column. */}
      {menuOpen && (
        <Menu onClose={() => setMenuOpen(false)}>
          <MenuItem icon="bookmark" onSelect={onAddToList}>
            Add to list
          </MenuItem>
          {/* Directly under the row it undoes: one operation and its inverse.
              The name is italic, quoted apart from the row's own words. */}
          {listName !== null && (
            <MenuItem icon="bookmarkMinus" onSelect={onRemoveFromList}>
              Remove from <em className={styles.listName}>{listName}</em>
            </MenuItem>
          )}

          <MenuSeparator />
          {/* The one group whose rows need a job behind the chat, so the one
              with a scope to state. Headed, so it is read before the rows. */}
          <MenuGroupTitle>For job messages ({stageReach})</MenuGroupTitle>
          {HIRING_STAGES.map((stage) => (
            <MenuItem
              key={stage}
              icon={STAGE_META[stage].icon}
              disabled={targets[stage].length === 0}
              onSelect={() => onAction(stage)}
            >
              {/* The verb the bare name lost: under a group title "Shortlist"
                  reads as a kind of chat, not as something this row does. */}
              Mark as {STAGE_META[stage].name}
            </MenuItem>
          ))}

          <MenuSeparator />
          {/* Named off what it will actually do, by `closingVariantFor`: a set
              with no job behind any of it rejects nobody. */}
          <MenuItem
            icon={ending.icon}
            danger
            disabled={targets.end.length === 0}
            onSelect={() => onAction('end')}
          >
            {ending.label}
          </MenuItem>
        </Menu>
      )}
    </div>
  );
}
